import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { useTheme, useMediaQuery, Box,   
  CircularProgress, Typography, Tooltip, IconButton, 
} from '@mui/material';
import DataGridComponent from 'components/DataGridComponent';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import UpdateIcon from '@mui/icons-material/Update';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import { hasPermission } from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const Countries = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "Countries", "create", false);
  const canEdit   = hasPermission(user?.permissions, "Countries", "update", false);
  const canDelete = hasPermission(user?.permissions, "Countries", "delete", false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [fileName, setFileName] = useState("");
  const [countryData, setCountryData] = useState([]);
  
  const [countries, setCountries] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handleCountries = async () => {
      try {
        setLoading(true);  
        const res = await api.get('/countries');
        const result = res.data || [];
  
        setCountries(result);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    
    handleCountries();
  }, []); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },    
    {
      field: "countryCode",
      headerName: "CountryCode",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),      
    },
    {
      field: "countryName",
      headerName: "CountryName",
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 3 };
      })(), 
    },
    {
      field: "nameLocal",
      headerName: "NameLocal",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(), 
    },
    {
      field: "continent",
      headerName: "Continent",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(), 
    },
    {
      field: "region",
      headerName: "Region",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 4 };
      })(), 
    },
    {
      field: "capitalName",
      headerName: "CapitalName",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(), 
    },
    {
      field: "travelZone",
      headerName: "TravelZone",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(), 
    },
    {
      field: "isDefault",
      headerName: "IsDefault",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1 };
      })(), 
    },
  ], [isMobile]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,      
      sortable: false,
      width: 90,      
      renderCell: (params) => {
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/countries/${params.row._id}`)
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);                  
                }}
              >
                <DeleteForeverIcon />
              </IconButton>                
            </Tooltip>          
          </Box>
        );
      },
    },
  ];

  const gridStyles = {
    "& .MuiDataGrid-row.isDefault-true": {
      backgroundColor: `${theme.palette.primary.light} !important`,  
    },
  };
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, countries);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/countries/delete/${idToDelete}`);

        setCountries(countries.filter((item) => item._id !== idToDelete));
        toast.success('Country deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, countries);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/countries/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setCountries(countries.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete countries!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    countries
  ]);
   
  const handleUpload = (event) => {
    const files = event.target.files;

    if (files.length === 0) return;
    setFileName(files[0].name);
    setLoading(true);

    const file = files[0];
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a .csv file.");
      setLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      try {
        const contents = e.target.result;

        // Split the CSV into rows
        const rows = contents.split(/\r?\n/);

        const convertedData = [];
        let region = "";

        rows.forEach((row) => {
          // Trim whitespace and skip empty rows
          const trimmedRow = row.trim();
          if (!trimmedRow) return;

          // Check if the row represents a category (starts with ",")
          if (trimmedRow.startsWith(",")) {
            region = trimmedRow
              .replace(/^,/, "") // Remove leading comma
              .replace(/"/g, "") // Remove surrounding quotes
              .trim();
          } else {
            const [countryCode, countryName] = trimmedRow.split(",");
            if (countryCode && countryName) {
              convertedData.push({
                region,
                countryCode: countryCode.trim(),
                countryName: countryName.trim(),
              });
            }
          }
        });
        
        setCountryData(convertedData);
        toast.success("File uploaded successfully!");
      } catch (error) {
        toast.error("Error parsing CSV file: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsText(file);
  };
  
  const handleUpdate = async () => {
    try {
      const url = '/countries/updatebyicao';
      const payload = countryData;
     
      await api.put(url, payload);

      toast.success('Countries updated successfully');
    } catch (error) {
      toast.success(error?.message || 'Failed to update countries!');
      setError(error);      
    }
  };


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Countries" 
          subtitle={`${loading ? 'Loading...' : 'List of Countries'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <label htmlFor={!!fileName ? undefined : "upload-file"}>
              <Tooltip title="Upload countries csv file" arrow>
                <span>
                  <StyledButton 
                    component="span" 
                    disabled={!!fileName}
                  >
                    <FileUploadIcon />
                  </StyledButton>  
              </span>
              </Tooltip>
            </label>
            <input
              id="upload-file"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />

            {canEdit && (
              <Tooltip title="Update from csv file" arrow>
                <span>
                  <StyledButton
                    disabled={countryData.length === 0}
                    onClick={handleUpdate}
                  >
                    <UpdateIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, countries)}
                    onClick={handleBulkDelete}
                  >
                    <DeleteIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/countries/new")}>
                    <AddCircleOutlineIcon />
                  </StyledButton>
                </span>
              </Tooltip>
            )}     
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={countries}
                primaryField="countryCode"
                fields={[
                  { label: "Country", key: "countryName" },
                  { label: "Continent", key: "continent" },
                  { label:"Region", key:"region" },
                  { label:"CapitalName", key:"capitalName" },
                  { label:"TravelZone", key:"travelZone" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/countries/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={countries || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isDefault === true ? 'isDefault-true' : '',
                sx: gridStyles,
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Countries;
